import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

// The only rule type available today is Field Mapping. New types get added to this union
// and to RULE_TYPE_OPTIONS below, and the panel branches on the selected value.
export type RuleType = "field_mapping";

type RuleTypeSelectorProps = {
    value: RuleType;
    // Fired when the user picks an option from the menu — the panel advances to that rule's form.
    onSelect: (value: RuleType) => void;
};

const RuleTypeSelector = ({ value, onSelect }: RuleTypeSelectorProps) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement | null>(null);

    const options: { value: RuleType; label: string }[] = [
        { value: "field_mapping", label: t("Field Mapping") },
    ];
    const selected = options.find((option) => option.value === value) ?? options[0];

    // Close the menu when clicking outside of it.
    useEffect(() => {
        if (!open) return;
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [open]);

    const handlePick = (next: RuleType) => {
        setOpen(false);
        onSelect(next);
    };

    return (
        <div className="bg-white border border-unlisted-gray-lines rounded p-4 flex flex-col gap-2" data-testid="RuleTypeSelector">
            <span className="text-sm font-semibold text-ui-text-primary-text">{t("Select rule type")}</span>
            <div className="relative" ref={containerRef}>
                <button
                    type="button"
                    onClick={() => setOpen((isOpen) => !isOpen)}
                    className="w-full flex items-center justify-between border border-unlisted-gray-lines rounded px-3 py-2 text-sm text-ui-text-primary-text bg-white hover:border-unlisted-medium-gray transition-colors duration-200"
                    aria-haspopup="listbox"
                    aria-expanded={open}
                    data-testid="RuleTypeSelector-toggle"
                >
                    <span>{selected.label}</span>
                    <span className="text-ui-text-primary-text text-xs">▾</span>
                </button>
                {open && (
                    <ul
                        role="listbox"
                        className="absolute z-10 mt-1 w-full bg-white border border-unlisted-gray-lines rounded shadow-md py-1"
                        data-testid="RuleTypeSelector-menu"
                    >
                        {options.map((option) => (
                            <li key={option.value} role="option" aria-selected={option.value === value}>
                                <button
                                    type="button"
                                    onClick={() => handlePick(option.value)}
                                    className="w-full text-left px-3 py-2 text-sm text-ui-text-primary-text hover:bg-unlisted-light-gray transition-colors duration-200"
                                    data-testid={`RuleTypeSelector-option-${option.value}`}
                                >
                                    {option.label}
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
};

export default RuleTypeSelector;
